import queryString from 'query-string'
import React, { RefObject, useEffect, useRef, useState } from 'react'
import className from 'classnames/bind'
import { useHistory, useLocation, useParams } from 'react-router'

import style from './MyhouseNotifySetMain.module.scss'

import BlindContent from '@Components/atoms/BlindContent'
import MyhouseNotifyToggle from '@Components/atoms/MyhouseNotifyToggle'
import MyhouseNotifyTabMenu from '@Components/atoms/MyhouseNotifyTabMenu'
import useNotify from '@Hooks/myhouse/notify/useNotify'
import { LAND_SERVICE_PREFIX_URL } from '@Root/contants/urlConstants'
import { FETCH_STATUS } from '@Root/contants'
import { MyhouseMainHouseResponseType } from '@Root/slices/myhouse/settingSlice'
import { NCLICK, NDS_PROTOCOL } from '@Root/contants/lcs'
import { handleNclickMix } from '@Helpers/nclick'
import { lcsSend } from '@Helpers/lcs'

const cx = className.bind(style)

export type houseType = {
  houseNo: number
  complexName: string
  etiquetteYn: boolean
  notifyYn: boolean
  articleNotifyYn: boolean
  realPriceNotifyYn: boolean
  leaseNotifyYn: boolean
  insuranceNotifyYn: boolean
  noticeNotifyYn: boolean
  essentialNotifyYn: boolean
}

type LocationStateType = {
  callBackUrl: string
}

const initialLocationState: LocationStateType = {
  callBackUrl: '',
}
const MyhouseNotifySetMain: React.FC = () => {
  //변수선언부
  const { reduxNotify, loadNotifyList, loadNotifyGet, loadNotify, loadNotifyEtiquette, setToggle, setCurrentHouseNo, setRouteHouseNo } = useNotify()
  const { tabList, notify, detail } = reduxNotify
  const history = useHistory()
  const { houseNo } = useParams<{ houseNo: string }>()
  const elementRef = useRef([])
  const location = useLocation()
  const state = location ? (location.state as LocationStateType) : initialLocationState
  const { callBackUrl } = state || ''
  const { search } = location
  const { back } = queryString.parse(search)

  //함수정의부
  const handleHouseClick = (event: React.MouseEvent<HTMLButtonElement>, houseNo: number) => {
    if (notify.fetchStatus !== FETCH_STATUS.PENDING) {
      setCurrentHouseNo({ currentHouseNo: houseNo })
      history.replace({
        pathname: `${LAND_SERVICE_PREFIX_URL.MYHOUSE}/notify/settings/${houseNo}`,
      })
    }
  }

  function notifyAction(key: string, value: boolean) {
    const param: { houseNo: number; notifyYn: boolean; [p: string]: number | boolean } = {
      houseNo: notify.houseNo,
      notifyYn: notify.notifyYn,
      [key]: value,
    }
    loadNotify(param)
  }

  function notifyBatchAction(key: string, value: boolean) {
    let notiParam = {}
    if (!value) {
      // 모든 noti false
      notiParam = {
        articleNotifyYn: false,
        realPriceNotifyYn: false,
        leaseNotifyYn: false,
        insuranceNotifyYn: false,
        noticeNotifyYn: false,
        essentialNotifyYn: false,
      }
    } else {
      notiParam = {
        articleNotifyYn: detail.isArticleNotifyYn, // 신규 매물 알림 : 상가, 건물 타입이 아닌경우 true
        realPriceNotifyYn: true, // 신규 실거래가 알림 : true
        leaseNotifyYn: detail.isTradeEndYmd, // 전월세 이고 만기일자 등록된 경우 : true
        insuranceNotifyYn: detail.isInsuranceYmd, // 보험 만기 일정이 등록된 경우 : true
        noticeNotifyYn: true, // 기본 공지사항 : true
        essentialNotifyYn: true, // 필독 공지사항 : true
      }
    }

    const param: { houseNo: number; notifyYn: boolean; [p: string]: number | boolean } = {
      houseNo: notify.houseNo,
      notifyYn: value,
      ...notiParam,
    }
    loadNotify(param)
  }

  const handleClick = (evt: React.SyntheticEvent<HTMLInputElement>) => {
    const target = evt.target as HTMLInputElement
    const key: string = target.name
    const value: boolean = target.checked

    setToggle({ key, value })

    if (key === 'notifyYn') {
      notifyBatchAction(key, value)
    } else {
      notifyAction(key, value)
    }
  }

  const handleEtiquetteClick = (evt: React.SyntheticEvent<HTMLInputElement>) => {
    const target = evt.target as HTMLInputElement
    const key: string = target.name
    const value: boolean = target.checked
    setToggle({ key, value })

    loadNotifyEtiquette({ etiquetteYn: value })
  }

  const handleMyHouseAlarmComplete = (evt: React.MouseEvent<HTMLAnchorElement>) => {
    evt.preventDefault()

    if ((document.referrer && document.referrer.includes('/myhouse')) || (back && back === 'myhouse')) {
      window.location.href = `${process.env.REACT_APP_LAND_WEB_URL}/myhouse${
        detail.currentHouseNo > 0 ? '/' + detail.currentHouseNo : ''
      }`
    } else {
      history.goBack()
    }
  }

  //useEffect
  useEffect(() => {
    lcsSend(NDS_PROTOCOL.MY_HOUSE.HOME.NOTIFY)
    setRouteHouseNo(Number(houseNo))
    loadNotifyList()
  }, [])

  useEffect(() => {
    if (detail.currentHouseNo === -1) {
      history.replace(`${LAND_SERVICE_PREFIX_URL.MYHOUSE}/regist/guide`)
    }
    if (detail.fetchStatus !== FETCH_STATUS.PENDING && detail.currentHouseNo > 0) {
      loadNotifyGet(detail.currentHouseNo)
    }
  }, [detail.currentHouseNo, detail.fetchStatus])

  return (
    <>
      {detail.currentHouseNo > 0 && (
        <>
          <header className={cx('myhouse_header')}>
            <div className={cx('myhouse_header_inner')}>
              <h1 className={cx('myhouse_header_title')}>우리집 알림 설정</h1>
              <a href="#" onClick={handleNclickMix(NCLICK.MY_HOUSE.NOTIFY.COMPLETE, handleMyHouseAlarmComplete)} className={cx('myhouse_header_link')}>
                완료
              </a>
            </div>
          </header>

          <section id="content" className={cx('content_myhouse', 'type_notify')} role="main">
            <BlindContent />
            <div className={cx('content_notify_inner')}>
              <MyhouseNotifyTabMenu buttonRef={elementRef} houses={tabList} handleHouseClick={handleHouseClick} selectHouseNo={detail.currentHouseNo} />
              {notify && detail.isNotifyExists ? (
                <>
                  <MyhouseNotifyToggle
                    name="notifyYn"
                    title={'알림받기'}
                    descType={'TOTAL'}
                    isToggleType={'TOGGLE'}
                    initValue={notify.notifyYn}
                    handleClick={handleNclickMix(NCLICK.MY_HOUSE.NOTIFY.NOTIFY_YN, handleClick)}
                  />
                  {notify.notifyYn === true && (
                    <>
                      {detail.isArticleNotifyYn && (
                        <MyhouseNotifyToggle
                          name="articleNotifyYn"
                          title={'신규 매물 알림'}
                          isToggleType={'TOGGLE'}
                          initValue={notify.articleNotifyYn}
                          handleClick={handleNclickMix(NCLICK.MY_HOUSE.NOTIFY.ARTICLE, handleClick)}
                          tooltipType={detail.isNaverDB ? 'newArticleEnrolled' : 'newArticle'}
                          nclickTooltip={NCLICK.MY_HOUSE.NOTIFY.TOOLTIP.ARTICLE}
                        />
                      )}
                      {detail.isNaverDB && (
                        <MyhouseNotifyToggle
                          name="realPriceNotifyYn"
                          title={'신규 실거래가 알림'}
                          isToggleType={'TOGGLE'}
                          initValue={notify.realPriceNotifyYn}
                          handleClick={handleNclickMix(NCLICK.MY_HOUSE.NOTIFY.REAL_PRICE, handleClick)}
                          tooltipType={'newRealPrice'}
                          nclickTooltip={NCLICK.MY_HOUSE.NOTIFY.TOOLTIP.REAL_PRICE}
                        />
                      )}
                      {!detail.isTradeOwner && (
                        <MyhouseNotifyToggle
                          houseNo={detail.currentHouseNo}
                          name="leaseNotifyYn"
                          title={'전월세 만기 일정 알림'}
                          actionTitle={'계약 만기일 입력하기'}
                          isToggleType={detail.isTradeEndYmd ? 'TOGGLE' : 'ACTION'}
                          actionType={'ROUTE'}
                          initValue={notify.leaseNotifyYn}
                          handleClick={handleNclickMix(NCLICK.MY_HOUSE.NOTIFY.EXPIRE, handleClick)}
                          nclickAction={NCLICK.MY_HOUSE.NOTIFY.EXPIRE_INPUT}
                        />
                      )}
                      {detail.isNaverDB && (
                        <>
                          <MyhouseNotifyToggle
                            name="essentialNotifyYn"
                            title={'필독 공지사항 알림'}
                            isToggleType={'TOGGLE'}
                            initValue={notify.essentialNotifyYn}
                            handleClick={handleNclickMix(NCLICK.MY_HOUSE.NOTIFY.NOTICE_ESSENTIAL, handleClick)}
                          />
                          <MyhouseNotifyToggle
                            name="noticeNotifyYn"
                            title={'기본 공지사항 알림'}
                            isToggleType={'TOGGLE'}
                            initValue={notify.noticeNotifyYn}
                            handleClick={handleNclickMix(NCLICK.MY_HOUSE.NOTIFY.NOTICE_BASE, handleClick)}
                          />
                        </>
                      )}
                    </>
                  )}
                </>
              ) : (
                <p className={cx('myhouse_notify_empty')}>설정할 수 있는 알림이 없습니다.</p>
              )}
            </div>
            {notify && detail.isNotifyExists && <p className={cx('myhouse_notify_text')}>한번에 많은 알림이 발생할 경우 일부 알림이 순간적으로 전송되지 않을 수 있습니다.</p>}
          </section>
        </>
      )}
    </>
  )
}

export default MyhouseNotifySetMain
